/**
 * What the teacher has narrowed the Lecture Record down to — docs/06-UI-SPEC.md §11.2.
 *
 * Kept away from React so the rules can be tested: what each quick filter means in
 * days, what the address the server is asked for looks like, and what the chips above
 * the results say. The searching itself happens in the database.
 */

/** The quick filters, in the order §11.2 lists them. */
export type QuickFilter = 'today' | 'week' | 'month' | 'starred';

export interface LectureFilters {
  readonly search: string;
  /** Epoch milliseconds, or null for "any time". */
  readonly from: number | null;
  readonly to: number | null;
  readonly student: string | null;
  readonly topic: string | null;
  readonly starredOnly: boolean;
  /** Which quick filter is lit, so clicking it again can turn it off. */
  readonly quick: QuickFilter | null;
}

export const NO_FILTERS: LectureFilters = {
  search: '',
  from: null,
  to: null,
  student: null,
  topic: null,
  starredOnly: false,
  quick: null,
};

/** True when nothing is narrowing the list. */
export function isUnfiltered(filters: LectureFilters): boolean {
  return (
    filters.search.trim() === '' &&
    filters.from === null &&
    filters.to === null &&
    filters.student === null &&
    filters.topic === null &&
    !filters.starredOnly
  );
}

/** Midnight at the start of the day this moment falls in, in the browser's own clock. */
function startOfDay(now: number): number {
  const day = new Date(now);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

/** How far back each quick filter reaches. */
function since(quick: QuickFilter, now: number): number | null {
  const midnight = startOfDay(now);
  const DAY = 24 * 60 * 60 * 1000;
  switch (quick) {
    case 'today':
      return midnight;
    case 'week':
      return midnight - 6 * DAY;
    case 'month':
      return midnight - 29 * DAY;
    case 'starred':
      return null;
  }
}

/**
 * Applies a quick filter, or takes it off when it was already the one lit.
 *
 * Only one is lit at a time: they are four answers to the same question, "how far
 * back", and Starred is the exception that carries no dates.
 */
export function toggleQuick(
  filters: LectureFilters,
  quick: QuickFilter,
  now: number,
): LectureFilters {
  if (filters.quick === quick) {
    return { ...filters, quick: null, from: null, to: null, starredOnly: false };
  }
  return {
    ...filters,
    quick,
    from: since(quick, now),
    to: null,
    starredOnly: quick === 'starred',
  };
}

/** The address the server is asked for. Empty values are left out entirely. */
export function filterQuery(filters: LectureFilters): string {
  const pairs: [string, string][] = [];
  const search = filters.search.trim();
  if (search !== '') pairs.push(['search', search]);
  if (filters.from !== null) pairs.push(['from', String(filters.from)]);
  if (filters.to !== null) pairs.push(['to', String(filters.to)]);
  if (filters.student !== null) pairs.push(['student', filters.student]);
  if (filters.topic !== null) pairs.push(['topic', filters.topic]);
  if (filters.starredOnly) pairs.push(['starred', 'true']);

  return pairs.length === 0 ? '' : `?${new URLSearchParams(pairs).toString()}`;
}

/** One removable chip above the results. */
export interface FilterChip {
  readonly key: string;
  readonly label: string;
}

const QUICK_LABELS: Readonly<Record<QuickFilter, string>> = {
  today: 'Today',
  week: 'This week',
  month: 'This month',
  starred: 'Starred',
};

/** What is currently narrowing the list, as chips the teacher can take off. */
export function activeChips(filters: LectureFilters): readonly FilterChip[] {
  const chips: FilterChip[] = [];
  const search = filters.search.trim();
  if (search !== '') chips.push({ key: 'search', label: `“${search}”` });
  if (filters.quick !== null) chips.push({ key: 'quick', label: QUICK_LABELS[filters.quick] });
  if (filters.student !== null) chips.push({ key: 'student', label: filters.student });
  if (filters.topic !== null) chips.push({ key: 'topic', label: filters.topic });
  if (filters.starredOnly && filters.quick !== 'starred') {
    chips.push({ key: 'starred', label: 'Starred' });
  }
  return chips;
}

/** Takes one chip off, leaving everything else exactly as it was. */
export function withoutChip(filters: LectureFilters, key: string): LectureFilters {
  switch (key) {
    case 'search':
      return { ...filters, search: '' };
    case 'quick':
      return { ...filters, quick: null, from: null, to: null, starredOnly: false };
    case 'student':
      return { ...filters, student: null };
    case 'topic':
      return { ...filters, topic: null };
    case 'starred':
      return { ...filters, starredOnly: false };
    default:
      return filters;
  }
}
